import type { CompletionRule, RequestStatus } from "@gw/shared-types";
import type { JourneyDetailDto, JourneyRequestRecordDto } from "@/api/endpoints";
import { fmtDateOnly, fmtET, fmtETDate } from "@/lib/format";
import { INTAKE_SOURCE_LABEL } from "@/features/operations/operationsLabels";
import { completionNote } from "@/features/requestDetail/completionNote";

interface Props {
  journey: JourneyDetailDto;
}

function pillOf(status: string | null | undefined): { cls: string; label: string } {
  switch (status) {
    case "Completed": return { cls: "done", label: "Completed" };
    case "InProgress": return { cls: "moving", label: "In progress" };
    default: return { cls: "outline", label: status ?? "—" };
  }
}

function requestLine(r: JourneyRequestRecordDto): string {
  const parts = [`Created ${fmtET(r.createdDateTime)}`];
  if (r.releasedDateTime) parts.push(`released ${fmtETDate(r.releasedDateTime)}`);
  if (r.requestCompletedDateTime) parts.push(`last task done ${fmtETDate(r.requestCompletedDateTime)}`);
  const note = completionNote({ completionRule: r.completionRule as CompletionRule | null, completedAt: r.completedAt ? new Date(r.completedAt) : null, status: r.status as RequestStatus });
  if (note) parts.push(note.replace(/\.$/, ""));
  else parts.push(`status ${r.status}`);
  if (r.assignedToUserName) parts.push(`owner ${r.assignedToUserName}`);
  return parts.join(" · ");
}

/**
 * Every record the journey touched, on one row: the intake, the pre-verification request, the request
 * the page is about, the work order and the other requests on it. Nothing here navigates away: the
 * page already holds what the old screens showed. The request the page is about is outlined.
 */
export function RecordsRow({ journey: d }: Props) {
  const carriedId = d.request?.id ?? null;
  const intake = d.intake;
  const intakePill = intake ? (intake.journeyState === "canceled" ? { cls: "outline", label: "Canceled" } : intake.isOpen ? { cls: "moving", label: "Open" } : { cls: "done", label: "Accepted" }) : null;
  const wo = d.workOrder;
  const woPill = wo ? (wo.offboardingCompletionDate ? { cls: "done", label: "Terminated" } : wo.approveDateTime ? { cls: "done", label: "Approved" } : wo.submitDateTime ? { cls: "att-blocked", label: wo.rejectCount > 0 ? "Sent back, resubmitted" : "Pending review" } : { cls: "outline", label: "Draft" }) : null;
  const primary = d.request && d.primaryRequestId == null ? d.request : null;
  return (
    <div className="section journey-records">
      <h3>Records on this journey <span className="sub">everything the journey touched, on one page</span></h3>
      <div className="rel">
        <div className={`rec${d.carrier === "intake" || d.carrier === "awaitingRequest" ? " current" : ""}`}>
          <div className="k">Intake{intakePill && <span className={`ops-pill ${intakePill.cls}`}>{intakePill.label}</span>}</div>
          {intake ? (
            <>
              <div className="v">{INTAKE_SOURCE_LABEL[intake.source]}{intake.sourceSystem ? ` · ${intake.sourceSystem.replace(/^Procom/i, "")}` : ""}{intake.sourceReference ? ` ${intake.sourceReference}` : ""}</div>
              <div className="d">
                Started {fmtET(intake.startedDateTime)} · status {intake.status ?? "unknown"} since {fmtETDate(intake.statusChangedDateTime)}
                {intake.endedDateTime ? ` · ended ${fmtETDate(intake.endedDateTime)}` : ""}
                {intake.assignedToName ? ` · assigned to ${intake.assignedToName}` : ""}
                {intake.jobTitle ? ` · ${intake.jobTitle}` : ""}
              </div>
            </>
          ) : (
            <><div className="v">None</div><div className="d">{d.kind === "Onboarding" || d.kind === "Redeployment" ? "No intake record is linked to this journey." : `${d.kind} requests start at the request.`}</div></>
          )}
        </div>
        <div className={`rec${d.carrier === "preOnboarding" ? " current" : ""}`}>
          <div className="k">Pre-Verification request{d.preOnboarding && <span className={`ops-pill ${pillOf(d.preOnboarding.calculatedStatus).cls}`}>{pillOf(d.preOnboarding.calculatedStatus).label}</span>}</div>
          {d.preOnboarding ? (
            <><div className="v">{d.preOnboarding.requestType} · {d.preOnboarding.id.slice(0, 13)}…</div><div className="d">{requestLine(d.preOnboarding)}</div></>
          ) : (
            <><div className="v">None</div><div className="d">{intake?.source === "ats" ? "ATS placements go straight to a work order a third of the time." : ""}</div></>
          )}
        </div>
        <div className={`rec${d.carrier === "request" && primary ? " current" : ""}`}>
          <div className="k">{d.kind} request{primary && <span className={`ops-pill ${pillOf(primary.calculatedStatus).cls}`}>{pillOf(primary.calculatedStatus).label}</span>}</div>
          {primary ? (
            <>
              <div className="v">{primary.requestType} · {primary.id.slice(0, 13)}…</div>
              <div className="d">
                Created {fmtET(primary.requestCreatedDateTime)}
                {primary.requestReleasedDateTime ? ` · released ${fmtETDate(primary.requestReleasedDateTime)}` : ""}
                {primary.requestCompletedDateTime ? ` · last task done ${fmtETDate(primary.requestCompletedDateTime)}` : ""}
                {` · status ${primary.status}`}
                {primary.assignedToUserName ? ` · owner ${primary.assignedToUserName}` : ""}
              </div>
            </>
          ) : d.primaryRequestId ? (
            <><div className="v">{d.primaryRequestId.slice(0, 13)}…</div><div className="d">Exists on the work order; the pre-verification carries the journey on this page.</div></>
          ) : (
            <><div className="v">Not created yet</div><div className="d">{wo ? `The journey waits for a request on WO ${wo.workOrderNumber ?? wo.workOrderId}.` : "No work order yet."}</div></>
          )}
        </div>
        <div className={`rec${d.carrier === "workOrder" ? " current" : ""}`}>
          <div className="k">Work order{woPill && <span className={`ops-pill ${woPill.cls}`}>{woPill.label}</span>}</div>
          {wo ? (
            <>
              <div className="v">WO {wo.workOrderNumber ?? wo.workOrderId}</div>
              <div className="d">
                Created {fmtETDate(wo.createdDateTime)} · submitted {wo.submitCount}× · sent back {wo.rejectCount}×
                {wo.lastActionDateTime ? ` · last action ${fmtETDate(wo.lastActionDateTime)}` : ""}
                {wo.lastModifiedDateTime ? ` · last edit ${fmtETDate(wo.lastModifiedDateTime)}` : ""}
                {wo.approveDateTime ? ` · approved ${fmtETDate(wo.approveDateTime)}${wo.approver ? ` by ${wo.approver}` : ""}` : " · approver —"}
                {wo.offboardingCompletionDate ? ` · terminated ${fmtETDate(wo.offboardingCompletionDate)}` : d.carrier === "workOrder" ? " · offboarding in progress" : ""}
                {wo.startDate ? ` · ${fmtDateOnly(wo.startDate)} → ${fmtDateOnly(wo.endDate)}` : ""}
              </div>
            </>
          ) : (
            <><div className="v">None</div><div className="d">No work order exists yet.</div></>
          )}
        </div>
        <div className="rec">
          <div className="k">Other requests on this WO{d.siblings.length > 0 && <span className="ops-pill moving">{d.siblings.length}</span>}</div>
          {d.siblings.length > 0 ? (
            d.siblings.map((s) => (
              <div key={s.id} className="d">
                <b>{s.requestType}</b> · {s.id.slice(0, 13)}… · {requestLine(s)}{s.id === carriedId ? " (this page)" : ""}
              </div>
            ))
          ) : (
            <><div className="v">None</div><div className="d">Extensions, change and document requests on this work order would be listed here.</div></>
          )}
        </div>
      </div>
    </div>
  );
}
